import { useState } from "react";
import { Loader2, Inbox as InboxIcon, Copy, Check, AlertTriangle, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export const card = "bg-white border border-slate-200 rounded-xl";
export const inputCls = "w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400/50 focus:border-emerald-400 transition-colors";
export const btn = "inline-flex items-center justify-center gap-2 h-9 px-4 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:pointer-events-none";
export const btnPrimary = `${btn} bg-[#128C7E] text-white hover:bg-[#075E54]`;
export const btnGhost = `${btn} border border-slate-200 bg-white text-slate-700 hover:bg-slate-50`;
export const btnDanger = `${btn} border border-rose-200 bg-white text-rose-600 hover:bg-rose-50`;

export function Logo({ collapsed }) {
  return (
    <div className="flex items-center gap-2.5" data-testid="chat360-logo">
      <div className="relative w-9 h-9 rounded-xl bg-[#075E54] flex items-center justify-center shrink-0">
        <span className="absolute inset-1 rounded-lg border-2 border-[#25D366] border-dashed animate-[spin_18s_linear_infinite]" />
        <span className="font-display font-extrabold text-white text-[13px]">360</span>
      </div>
      {!collapsed && (
        <div className="leading-tight">
          <div className="font-display font-extrabold text-slate-900 text-lg tracking-tight">Chat<span className="text-[#128C7E]">360</span></div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold">Attendy Technologies</div>
        </div>
      )}
    </div>
  );
}

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 fade-up">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900" data-testid="page-title">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1 max-w-2xl">{subtitle}</p>}
      </div>
      {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
    </div>
  );
}

const BADGE = {
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  red: "bg-rose-50 text-rose-700 border-rose-200",
  violet: "bg-violet-50 text-violet-700 border-violet-200",
  blue: "bg-sky-50 text-sky-700 border-sky-200",
  gray: "bg-slate-100 text-slate-600 border-slate-200",
};
const STATUS_COLOR = {
  connected: "green", approved: "green", delivered: "green", read: "green", open: "green", active: "green", completed: "green", processed: "green", ok: "green", verified: "green", received: "blue", sent: "blue", running: "blue",
  pending: "amber", queued: "amber", scheduled: "amber", unverified: "amber", retrying: "amber", draft: "gray", local_draft: "gray", resolved: "gray", cancelled: "gray",
  failed: "red", error: "red", rejected: "red", submission_failed: "red", failing: "red", revoked: "red",
};
export function Badge({ children, color, testid }) {
  const c = color || STATUS_COLOR[String(children).toLowerCase().replace(/ /g, "_")] || "gray";
  return <span data-testid={testid} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap ${BADGE[c]}`}>{children}</span>;
}

export function DemoBadge() {
  return <Badge color="violet"><FlaskConical className="w-3 h-3" />Demo</Badge>;
}

export function Loading({ label = "Loading" }) {
  return <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-400" data-testid="loading-state"><Loader2 className="w-4 h-4 animate-spin" />{label}…</div>;
}

export function ErrorState({ message, onRetry }) {
  return (
    <div className="flex items-start gap-3 p-4 rounded-xl border border-rose-200 bg-rose-50 text-sm text-rose-700" data-testid="error-state">
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
      <div className="flex-1">{message}</div>
      {onRetry && <button className="font-semibold underline" onClick={onRetry}>Retry</button>}
    </div>
  );
}

export function Empty({ icon: Icon = InboxIcon, title, text, children, testid = "empty-state" }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-14 px-6" data-testid={testid}>
      <div className="w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center mb-3"><Icon className="w-5 h-5 text-[#128C7E]" /></div>
      <div className="font-display font-bold text-slate-800">{title}</div>
      {text && <p className="text-sm text-slate-500 mt-1 max-w-sm">{text}</p>}
      {children && <div className="mt-4">{children}</div>}
    </div>
  );
}

export function CopyButton({ value, testid = "copy-btn" }) {
  const [done, setDone] = useState(false);
  return (
    <button type="button" data-testid={testid} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500" title="Copy"
      onClick={() => { navigator.clipboard?.writeText(value); setDone(true); toast.success("Copied"); setTimeout(() => setDone(false), 1500); }}>
      {done ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

export function Code({ children, className = "" }) {
  return <pre className={`bg-slate-900 text-emerald-300 text-xs rounded-lg p-4 overflow-auto scroll-thin whitespace-pre-wrap break-all ${className}`}>{children}</pre>;
}

export function Confirm({ trigger, title, description, onConfirm, confirmLabel = "Confirm", testid = "confirm-action" }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${testid}-cancel`}>Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid={`${testid}-confirm`} className="bg-rose-600 hover:bg-rose-700" onClick={onConfirm}>{confirmLabel}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function Field({ label, hint, children }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-semibold text-slate-600">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-slate-400">{hint}</span>}
    </label>
  );
}

export function StatCard({ label, value, icon: Icon, tone = "emerald", sub, testid }) {
  const tones = { emerald: "text-[#128C7E] bg-emerald-50", blue: "text-sky-600 bg-sky-50", amber: "text-amber-600 bg-amber-50", red: "text-rose-600 bg-rose-50", violet: "text-violet-600 bg-violet-50", slate: "text-slate-600 bg-slate-100" };
  return (
    <div className={`${card} p-4 hover:-translate-y-0.5 hover:shadow-sm transition-transform`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-500">{label}</span>
        {Icon && <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${tones[tone]}`}><Icon className="w-3.5 h-3.5" /></span>}
      </div>
      <div className="font-display text-2xl font-extrabold text-slate-900 mt-2 tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

export function DemoBanner({ show }) {
  if (!show) return null;
  return (
    <div className="flex items-start gap-3 px-4 py-3 rounded-xl border border-violet-200 bg-violet-50 text-sm text-violet-800" data-testid="demo-mode-banner">
      <FlaskConical className="w-4 h-4 mt-0.5 shrink-0" />
      <div><b>Demo mode.</b> No Meta WhatsApp account is connected yet, so outgoing messages are simulated and records marked <b>DEMO</b> are sample data. Connect credentials in <a href="/whatsapp-accounts" className="underline font-semibold">WhatsApp Accounts</a> to go live.</div>
    </div>
  );
}

export function Modal({ open, onClose, title, children, wide, testid = "modal" }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm overflow-y-auto" onMouseDown={onClose}>
      <div data-testid={testid} onMouseDown={(e) => e.stopPropagation()} className={`bg-white rounded-2xl shadow-xl w-full ${wide ? "max-w-3xl" : "max-w-lg"} fade-up my-8`}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="font-display font-bold text-slate-900">{title}</h3>
          <button data-testid={`${testid}-close`} onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl leading-none">×</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
